// ADDED: holds the light/dark preference for the whole app.
// It does one thing: add or remove the `dark` class on <html>. Tailwind's `dark:`
// variants and the CSS variables in App.css both hang off that one class, so this
// is the only place the theme is decided.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Same key the inline script in public/index.html reads, so the theme is applied
// before React mounts and there is no white flash on load.
export const THEME_STORAGE_KEY = 'cloudvault_theme';

const ThemeContext = createContext();

// Whatever the inline script already decided is the truth on first render
function getInitialTheme() {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === 'dark' || saved === 'light') return saved;
        // No saved choice yet: follow the device setting
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch (error) {
        return 'light'; // storage blocked (private browsing) - light is the safe default
    }
}

export default function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(getInitialTheme);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle('dark', theme === 'dark');

        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch (error) {
            /* nothing to do - the theme still applies for this visit */
        }

        // Keeps the phone's browser chrome (address bar) in step with the page
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', theme === 'dark' ? '#0d1425' : '#f3f4f6');
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
